import type { AiSdkUIMessageChunk } from "@mui/x-chat"
import type { ChatDraftAttachment } from "@mui/x-chat-headless"
import { TermsSignaturePendingError, isAuthFailureResponse, withTokenRetry } from "api/with-token-retry"
import { ATTACHMENT_REJECTION_MESSAGES, INVALID_ATTACHMENT_FILENAME_MESSAGE, isAttachmentFilenameValid } from "./attachments-config"

export interface Attachment {
  id: string
  filename: string
  mediaType: string
  sizeBytes: number
}

export interface AttachmentAuth {
  account: string
  userId: string
  getNeedSignTerms: () => boolean
}

export class AttachmentUploadError extends Error {
  code: string | null

  constructor(message: string, code: string | null) {
    super(message)
    this.name = "AttachmentUploadError"
    this.code = code
  }
}

export const UPLOAD_FAILED_MESSAGE = "The upload did not complete. Attach the file again."

const UPLOAD_MESSAGES: Record<string, string> = {
  attachment_missing_file: "That file could not be read. Attach it again.",
  attachment_invalid_filename: INVALID_ATTACHMENT_FILENAME_MESSAGE,
  attachment_too_large: ATTACHMENT_REJECTION_MESSAGES["file-size"],
  attachment_inflight_limit: "Too many uploads at once. Wait a moment, then attach the file again.",
  attachment_record_failed: UPLOAD_FAILED_MESSAGE,
  managed_agent_disabled: "File uploads are not available on this account.",
}

const GONE_ATTACHMENT_CODES: ReadonlySet<string> = new Set(["CHAT_ATTACHMENT_OBJECT_MISSING", "CHAT_ATTACHMENT_NOT_FOUND"])

interface AuthedFetchParams {
  url: string
  body: FormData
  signal: AbortSignal
  auth: AttachmentAuth
}

function authedFetch({ url, body, signal, auth }: AuthedFetchParams): Promise<Response> {
  return withTokenRetry({
    account: auth.account,
    userId: auth.userId,
    getNeedSignTerms: auth.getNeedSignTerms,
    attempt: (token) => fetch(url, {
      method: "POST",
      credentials: "same-origin",
      headers: { Accept: "application/json", "X-OmegaPoint-Code": token },
      body,
      signal,
    }),
    isAuthFailure: isAuthFailureResponse,
    onRefreshBlocked: () => { throw new TermsSignaturePendingError() },
  })
}

function blockedTypeMessage(body: Record<string, unknown>): string {
  const extension = body.extension
  if (body.reason === "signature") return "That file looks like a program and is not accepted."
  if (typeof extension === "string" && extension.length > 0) return `${extension} files are not accepted.`
  return "That file type is not accepted."
}

async function uploadFailure(response: Response): Promise<AttachmentUploadError> {
  const body: unknown = await response.json().catch(() => null)
  if (typeof body !== "object" || body === null) return new AttachmentUploadError(UPLOAD_FAILED_MESSAGE, null)
  const record = body as Record<string, unknown>
  const code = typeof record.error === "string" ? record.error : null
  if (code === "attachment_blocked_type") return new AttachmentUploadError(blockedTypeMessage(record), code)
  if (code !== null && UPLOAD_MESSAGES[code] !== undefined) return new AttachmentUploadError(UPLOAD_MESSAGES[code], code)
  return new AttachmentUploadError(UPLOAD_FAILED_MESSAGE, code)
}

export function narrowAttachment(value: unknown): Attachment | null {
  if (typeof value !== "object" || value === null) return null
  const record = value as Record<string, unknown>
  if (typeof record.id !== "string") return null
  if (typeof record.filename !== "string") return null
  if (typeof record.mediaType !== "string") return null
  if (typeof record.sizeBytes !== "number") return null
  return { id: record.id, filename: record.filename, mediaType: record.mediaType, sizeBytes: record.sizeBytes }
}

export interface UploadAttachmentParams {
  accountAlias: string
  threadId: string | null
  file: File
  signal: AbortSignal
  auth: AttachmentAuth
}

export async function uploadAttachment({ accountAlias, threadId, file, signal, auth }: UploadAttachmentParams): Promise<Attachment> {
  if (!isAttachmentFilenameValid(file.name)) {
    throw new AttachmentUploadError(INVALID_ATTACHMENT_FILENAME_MESSAGE, "attachment_invalid_filename")
  }

  const base = `/api/${encodeURIComponent(accountAlias)}/ai/chat/threads`
  const url = threadId === null ? `${base}/attachments` : `${base}/${encodeURIComponent(threadId)}/attachments`
  const body = new FormData()
  body.append("file", file, file.name)

  const response = await authedFetch({ url, body, signal, auth })
  if (!response.ok) throw await uploadFailure(response)

  const payload: unknown = await response.json().catch(() => null)
  const attachment = typeof payload === "object" && payload !== null
    ? narrowAttachment((payload as Record<string, unknown>).attachment)
    : null
  if (attachment === null) throw new AttachmentUploadError(UPLOAD_FAILED_MESSAGE, null)
  return attachment
}

export function attachmentGoneId(error: unknown): string | null {
  if (typeof error !== "object" || error === null) return null
  const failure = error as { code?: unknown, error?: unknown }
  if (failure.code !== 409) return null
  if (typeof failure.error !== "object" || failure.error === null) return null
  const upstream = failure.error as { code?: unknown, details?: unknown }
  if (typeof upstream.code !== "string" || !GONE_ATTACHMENT_CODES.has(upstream.code)) return null
  if (typeof upstream.details !== "object" || upstream.details === null) return null
  const attachmentId = (upstream.details as { attachmentId?: unknown }).attachmentId
  return typeof attachmentId === "string" ? attachmentId : null
}

export function settleOrAbort<T>(pending: Promise<T>, signal: AbortSignal): Promise<T> {
  if (signal.aborted) return Promise.reject(new DOMException("Aborted", "AbortError"))
  return new Promise<T>((resolve, reject) => {
    const onAbort = () => reject(new DOMException("Aborted", "AbortError"))
    signal.addEventListener("abort", onAbort, { once: true })
    pending.then(resolve, reject).finally(() => signal.removeEventListener("abort", onAbort))
  })
}

export interface OpenTurnWithAttachmentRetryParams {
  openTurn: (attachmentIds: string[]) => ReadableStream<AiSdkUIMessageChunk>
  attachmentIds: string[]
  drafts: readonly ChatDraftAttachment[]
  reupload: (draft: ChatDraftAttachment) => Promise<string>
  signal: AbortSignal
}

export async function openTurnWithAttachmentRetry(
  { openTurn, attachmentIds, drafts, reupload, signal }: OpenTurnWithAttachmentRetryParams,
): Promise<ReadableStream<AiSdkUIMessageChunk>> {
  let reader = openTurn(attachmentIds).getReader()
  let pending: Awaited<ReturnType<typeof reader.read>> | null = null
  let retryFailure: { error: unknown } | null = null
  try {
    pending = await reader.read()
  } catch (error) {
    const goneId = attachmentGoneId(error)
    const index = goneId === null ? -1 : attachmentIds.indexOf(goneId)
    if (signal.aborted || index < 0 || drafts[index] === undefined) throw error
    const replacementId = await reupload(drafts[index])
    const nextIds = [...attachmentIds]
    nextIds[index] = replacementId
    reader = openTurn(nextIds).getReader()
    try {
      pending = await reader.read()
    } catch (retryError) {
      retryFailure = { error: retryError }
    }
  }

  return new ReadableStream<AiSdkUIMessageChunk>({
    async pull(controller) {
      if (retryFailure !== null) {
        controller.error(retryFailure.error)
        return
      }
      const settled = pending ?? await reader.read()
      pending = null
      if (settled.done) {
        controller.close()
        return
      }
      controller.enqueue(settled.value)
    },
    cancel(reason) {
      return reader.cancel(reason)
    },
  })
}
